import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { t } from '../i18n/nl.js';

export default function LoginPage() {
  const [mode, setMode] = useState('login'); // 'login' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { fetchRole } = useAuth();
  const { showSuccess } = useToast();

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { error: authError } = await supabase.auth.signInWithPassword({ email, password });
      if (authError) throw authError;

      const role = await fetchRole();

      if (['superadmin', 'admin_coach', 'coach'].includes(role)) {
        navigate('/coach/rooster', { replace: true });
      } else if (role === 'client') {
        navigate('/client/rooster', { replace: true });
      } else {
        navigate('/geen-toegang', { replace: true });
      }
    } catch (err) {
      setError(err.message === 'Invalid login credentials'
        ? t('auth.errorInvalidCredentials')
        : err.message || t('auth.errorGeneric'));
    } finally {
      setLoading(false);
    }
  }

  // Zelfde publieke route als de mobiele app inmiddels gebruikt
  // (useAuth.resetPassword) — via window.location.origin automatisch
  // domein-correct, want de browser staat al op het juiste domein
  // (app.fitensterker.nl productie, app.connect2web.nl staging).
  async function handleForgotPassword(e) {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError(t('auth.errorGeneric'));
      return;
    }
    setLoading(true);
    try {
      const { error: authError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-wachtwoord`,
      });
      if (authError) throw authError;
      showSuccess(t('auth.resetEmailSent'));
      setMode('login');
      setEmail('');
    } catch (err) {
      setError(err.message || t('auth.errorResetGeneric'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--color-bg)',
      padding: 20,
    }}>
      <div style={{ width: '100%', maxWidth: 400 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{
            width: 52,
            height: 52,
            background: 'var(--color-primary)',
            borderRadius: 14,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            marginBottom: 16,
          }}>
            💪
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-text)' }}>
            {t('app.name')}
          </h1>
          <p style={{ color: 'var(--color-text-muted)', marginTop: 6, fontSize: 14 }}>
            {t('auth.coachPortalTitle')}
          </p>
        </div>

        <div className="card">
          {mode === 'login' ? (
            <form onSubmit={handleSubmit}>
              {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}

              <div className="form-group">
                <label className="form-label" htmlFor="email">{t('auth.email')}</label>
                <input
                  id="email"
                  type="email"
                  className="form-input"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder={t('auth.emailPlaceholder')}
                  required
                  autoFocus
                />
              </div>

              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label" htmlFor="password">{t('auth.password')}</label>
                <input
                  id="password"
                  type="password"
                  className="form-input"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>

              <div style={{ textAlign: 'right', marginBottom: 20 }}>
                <button
                  type="button"
                  onClick={() => { setError(''); setMode('forgot'); }}
                  style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-primary)', fontSize: 13, cursor: 'pointer' }}
                >
                  {t('auth.forgotPasswordQ')}
                </button>
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center', padding: '11px 16px', fontSize: 15 }}
                disabled={loading}
              >
                {loading ? <><span className="spinner" style={{ width: 16, height: 16 }} /> {t('auth.loading')}</> : t('auth.login')}
              </button>
            </form>
          ) : (
            <form onSubmit={handleForgotPassword}>
              <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-text)', marginBottom: 4 }}>
                {t('auth.forgotPasswordTitle')}
              </h2>
              <p style={{ color: 'var(--color-text-muted)', fontSize: 13, marginBottom: 16 }}>
                {t('auth.forgotPasswordSub')}
              </p>

              {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}

              <div className="form-group" style={{ marginBottom: 24 }}>
                <label className="form-label" htmlFor="forgotEmail">{t('auth.email')}</label>
                <input
                  id="forgotEmail"
                  type="email"
                  className="form-input"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder={t('auth.emailPlaceholder')}
                  required
                  autoFocus
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center', padding: '11px 16px', fontSize: 15 }}
                disabled={loading}
              >
                {loading ? <><span className="spinner" style={{ width: 16, height: 16 }} /> {t('auth.loading')}</> : t('auth.sendReset')}
              </button>

              <button
                type="button"
                onClick={() => { setError(''); setMode('login'); }}
                style={{ width: '100%', textAlign: 'center', marginTop: 12, background: 'none', border: 'none', padding: 0, color: 'var(--color-text-muted)', fontSize: 13, cursor: 'pointer' }}
              >
                {t('auth.backToLogin')}
              </button>
            </form>
          )}
        </div>

        <p style={{ textAlign: 'center', marginTop: 20, fontSize: 12, color: 'var(--color-text-muted)' }}>
          {t('auth.loginFooter')}
        </p>
      </div>
    </div>
  );
}
